import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BFF_BASE_URL } from '../services/config';
import { useSessionStore } from '../stores/sessionStore';
import { useRealtimeStore } from '../stores/realtimeStore';
import { useAgentStore } from '../stores/useAgentStore';
import { toast } from 'sonner';

export function useRealtime() {
  const queryClient = useQueryClient();
  const session = useSessionStore((s) => s.session);
  const token = session?.access_token;
  const addThought = useAgentStore((state) => state.addThought);
  const registerAgent = useAgentStore((state) => state.registerAgent);

  useEffect(() => {
    if (!token) return;

    const query = new URLSearchParams({ token });
    const eventSource = new EventSource(`${BFF_BASE_URL}/v1/app/realtime?${query}`);

    eventSource.addEventListener('connected', () => {
      console.info('[SSE] Realtime Hub conectado.');
    });

    eventSource.addEventListener('message', (event) => {
      const message = parseEvent(event);
      if (!message) return;
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages', message.conversation_id] });
      window.dispatchEvent(new CustomEvent('crm-realtime-message', { detail: message }));
    });

    eventSource.addEventListener('notification', (event) => {
      const notification = parseEvent(event);
      if (!notification) return;
      toast(notification.title, {
        description: notification.content || notification.message,
      });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    eventSource.addEventListener('typing', (event) => {
      const data = parseEvent(event);
      if (!data) return;
      useRealtimeStore.getState().setTyping(data.conversation_id, data.users);
    });

    eventSource.addEventListener('agent_thought', (event) => {
      const data = parseEvent(event);
      if (!data) return;
      const agentName = data.agent_name || data.agentName;
      addThought({
        agentName, thought: data.thought, type: data.type, confidence: data.confidence,
      });
      registerAgent(agentName);
    });

    eventSource.addEventListener('deal_updated', () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    });

    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        console.warn('[SSE] Realtime Hub encerrado.');
      }
    };

    return () => {
      eventSource.close();
    };
  }, [token, queryClient, addThought, registerAgent]);
}

function parseEvent(event: MessageEvent) {
  try {
    return JSON.parse(event.data);
  } catch {
    console.warn('[SSE] Evento inválido ignorado.');
    return null;
  }
}
